import * as _ from 'styled-components/cssprop';
import React, { useState, FC, useEffect } from 'react';
import { AxiosError } from 'axios';
import { Snackbar } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Alert from '@material-ui/lab/Alert';
import 'react-phone-input-2/lib/style.css';
import { Meta } from 'types/meta';
import { getTranslation } from 'localization/helpers';
import { api } from 'services/api';
import { Framework } from 'client/components/Framework';
import { Head } from 'common/components/Head';
import { CompanyName } from 'styles/CompanyName';
import { QrType } from 'types/qrType';
import { initialCompany, initialReview } from 'services/initialStates';
import { FirstStage } from 'client/components/FormComponents/Stages/FirstStage';
import { Company } from 'types/company';
import { Location, NetworkReference } from 'types/location';
import { Place } from 'types/place';
import { Review } from 'types/review';
import { TagsStage } from 'client/components/FormComponents/Stages/TagsStage';
import { FinalStage } from 'client/components/FormComponents/Stages/FinalStage';
import { ContactsStage } from 'client/components/FormComponents/Stages/ContactsStage';
import { isCompany, isLocation, isPlace } from 'types/entity';
import { AdditionalQuestion } from 'types/additionalQuestion';

interface FormProps {
  entity: Company | Location | Place;
  meta: Meta;
  qrType: QrType;
  preview: boolean;
}

export const FormPage: FC<FormProps> = ({ entity, meta, qrType, preview }) => {
  const { t, i18n } = useTranslation();
  const [assessmentQuestion, setAssessmentQuestion] = useState<string | null>('');
  const [reviewSending, setReviewSending] = useState(false);
  const [reviewSendingError, setReviewSendingError] = useState(false);
  const [reviewSendingErrorText, setReviewSendingErrorText] = useState('');
  const [company, setCompany] = useState(initialCompany);
  const [locations, setLocations] = useState<Location[]>([]); // present only for a company
  const [ratingLinks, setRatingLinks] = useState<NetworkReference[]>([]); // present only for a location
  const [finalMessageTitle, setFinalMessageTitle] = useState<string | null>('');
  const [finalMessageBody, setFinalMessageBody] = useState<string | null>('');
  const [review, setReview] = useState<Review>(initialReview);

  const parseAdditionalQuestions = (questions: AdditionalQuestion[]) => {
    return questions.map(q => ({
      translations: q.translations,
      tagId: q.tag_id,
      rating: q.is_NPS ? 10 : -1,
      ratingError: false,
      isNPS: q.is_NPS
    }));
  };

  useEffect(() => {
    if (isCompany(entity)) {
      setCompany(entity);
      setLocations(entity.locations);
      setReview({
        ...review,
        contacts_required: locations[0]?.contacts_required,
        ratingIncreaseStrategy:
          locations[0]?.review_mechanics === 'rating_increase',
        additionalQuestions: parseAdditionalQuestions(
          entity.additional_questions
        )
      });
      const title = getTranslation(
        entity.translations,
        'final_message_title',
        i18n.language,
        meta.default_locale
      );
      const body = getTranslation(
        entity.translations,
        'final_message_body',
        i18n.language,
        meta.default_locale
      );
      const assessmentQuestion = getTranslation(
        company.translations,
        'assessment_question',
        i18n.language,
        meta.default_locale
      );
      setFinalMessageTitle(title);
      setFinalMessageBody(body);
      setAssessmentQuestion(assessmentQuestion);
    } else if (isLocation(entity)) {
      setCompany(entity.company);
      setRatingLinks(entity.location_rating_links);
      setReview({
        ...review,
        contacts_required: entity.contacts_required,
        ratingIncreaseStrategy: entity.review_mechanics === 'rating_increase',
        additionalQuestions: parseAdditionalQuestions(
          entity.additional_questions
        )
      });
      const title = getTranslation(
        entity.translations,
        'final_message_title',
        i18n.language,
        meta.default_locale
      );
      const body = getTranslation(
        entity.translations,
        'final_message_body',
        i18n.language,
        meta.default_locale
      );
      const assessmentQuestion = getTranslation(
        entity.translations,
        'assessment_question',
        i18n.language,
        meta.default_locale
      );
      setFinalMessageTitle(title);
      setFinalMessageBody(body);
      setAssessmentQuestion(assessmentQuestion);
    } else {
      setCompany(entity.company);
      setReview({
        ...review,
        contacts_required: entity.location.contacts_required
      });
      const title = getTranslation(
        entity.translations,
        'final_message_title',
        i18n.language,
        meta.default_locale
      );
      const body = getTranslation(
        entity.translations,
        'final_message_body',
        i18n.language,
        meta.default_locale
      );
      const assessmentQuestion = getTranslation(
        entity.location.translations,
        'assessment_question',
        i18n.language,
        meta.default_locale
      );
      setFinalMessageTitle(title);
      setFinalMessageBody(body);
      setAssessmentQuestion(assessmentQuestion);
    }
  }, [company, i18n.language]);

  const nextStage = async (): Promise<void> => {
    if (preview) return;
    switch (review.state) {
      case 'first':
        if (review.ratingIncreaseStrategy) {
          await sendReview();
          setReview({ ...review, state: 'finish' });
        } else if (review.additionalQuestions.length > 0) {
          setReview({ ...review, state: 'tags' });
        } else if (review.contacts_required !== 'no') {
          setReview({ ...review, state: 'contacts' });
        } else {
          await sendReview();
          setReview({ ...review, state: 'finish' });
        }
        break;
      case 'contacts':
        await sendReview();
        setReview({ ...review, state: 'finish' });
        break;
      case 'tags':
        if (review.contacts_required !== 'no') {
          setReview({ ...review, state: 'contacts' });
        } else {
          await sendReview();
          setReview({ ...review, state: 'finish' });
        }
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Something went wrong with state machine');
    }
  };

  const sendReview = async (): Promise<void> => {
    const payload = new FormData();
    payload.append('body', review.body);
    payload.append('rating', (review.rating + 1).toString());
    payload.append('name', review.name);
    payload.append('order_type', qrType);
    payload.append('phone', review.phone);
    payload.append('company_hash', company.hash);
    if (isLocation(entity)) payload.append('location_hash', entity.hash);
    if (isPlace(entity)) {
      payload.append('location_hash', entity.location.hash);
      payload.append('place_hash', entity.hash);
    }
    if (review.photo) payload.append('image', review.photo);
    if (review.ratingIncreaseStrategy) {
      payload.append(
        'additional_questions',
        JSON.stringify(
          review.additionalQuestions.map(q => ({
            tag_id: q.tagId,
            rating: q.isNPS ? q.rating : q.rating + 1
          }))
        )
      );
    }
    setReview({ ...review, sending: true });
    return api()
      .sendReview(payload)
      .then(data => {
        setReview(r => ({
          ...r,
          uuid: data.success
        }));
      })
      .catch((error: AxiosError) => {
        error.response?.status === 413
          ? setReviewSendingErrorText(t('fileTooLarge'))
          : setReviewSendingErrorText(t('errorTryAgain'));
        setReviewSendingError(true);
      })
      .finally(() => setReview({ ...review, sending: false }));
  };

  const companyName = getTranslation(
    company.translations,
    'title',
    i18n.language,
    meta.default_locale
  );

  const renderSwitch = (state: string): JSX.Element => {
    switch (state) {
      case 'first':
        return (
          <FirstStage
            meta={meta}
            locations={locations}
            ratingLinks={ratingLinks}
            review={review}
            setReview={setReview}
            assessment_question={assessmentQuestion}
            nextStage={nextStage}
          />
        );
      case 'tags':
        return (
          <TagsStage
            meta={meta}
            review={review}
            setReview={setReview}
            nextStage={nextStage}
          />
        );
      case 'contacts':
        return (
          <ContactsStage
            meta={meta}
            review={review}
            setReview={setReview}
            countryCode={company.country_code}
            nextStage={nextStage}
          />
        );
      default:
        return <FinalStage title={finalMessageTitle} body={finalMessageBody} />;
    }
  };

  const metaTitle = `${t('leaveFeedbackForCompany')} ${companyName}`;
  const metaDescription = `${t(
    'youCanLeaveFeedbackForCompany'
  )} ${companyName} ${t('viaRevizo')}`;

  return (
    <>
      <Framework locales={meta.locales} logo={meta.logo} color={meta.color}>
        <Head title={metaTitle} description={metaDescription} />
        {companyName && <CompanyName>{companyName}</CompanyName>}
        {renderSwitch(review.state)}
      </Framework>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={reviewSendingError}
        autoHideDuration={3000}
        onClose={() => setReviewSendingError(false)}
      >
        <Alert severity="error">{reviewSendingErrorText}</Alert>
      </Snackbar>
    </>
  );
};
